import type { QueryClient } from '@tanstack/react-query';
import type { Atom } from 'jotai/vanilla';
import { createElement, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { chronicleIsRecordingAtom } from '@/atoms/chronicle/session';
import { isSharedRecordingTimerRunning } from '@/models/timerModel';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type SessionResponse } from '@heidi/session/contracts';
import { activeSessionIdAtom } from '@heidi/session/data';

const messages = defineMessages({
  recordingInProgressTitle: {
    defaultMessage: 'Recording in progress',
    id: '/XISXx48UN',
    description:
      'Toast title shown when a new session is refused during an active recording.',
  },
  recordingInProgressDescription: {
    defaultMessage: 'You need to end the recording before switching to another session.',
    id: 'aTYaWTSQul',
    description:
      'Toast description shown when a new session is refused during an active recording.',
  },
});

export type RecordingGuardSignal = 'chronicle' | 'timer' | 'active_session';

export class RecordingActiveError extends Error {
  readonly signal: RecordingGuardSignal;

  constructor(signal: RecordingGuardSignal) {
    super('A recording is in progress.');
    this.name = 'RecordingActiveError';
    this.signal = signal;
  }
}

type RecordingGuardOptions = {
  jotaiStore: { get: <Value>(atom: Atom<Value>) => Value };
  queryClient: Pick<QueryClient, 'getQueryData'>;
};

export function getActiveRecordingSignal({
  jotaiStore,
  queryClient,
}: RecordingGuardOptions): RecordingGuardSignal | null {
  if (jotaiStore.get(chronicleIsRecordingAtom)) return 'chronicle';
  if (isSharedRecordingTimerRunning()) return 'timer';

  const activeSessionId = jotaiStore.get(activeSessionIdAtom);
  if (!activeSessionId) return null;

  const session = queryClient.getQueryData<SessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId: activeSessionId },
  ]);
  if (session?.recording_status === RecordingStatus.RECORDING) {
    return 'active_session';
  }

  if (session?.recording_status !== RecordingStatus.PAUSED) {
    logger.info('NEW_SESSION_RECORDING_GUARD_UNCORROBORATED', {
      scribe_session_id: activeSessionId,
    });
  }
  return null;
}

export function useRecordingActiveRefusal() {
  return useCallback((entryPoint: string | undefined, signal: RecordingGuardSignal) => {
    logger.info('NEW_SESSION_REFUSED_RECORDING_ACTIVE', { entryPoint, signal });
    toast({
      tone: 'destructive',
      title: createElement(FormattedMessage, messages.recordingInProgressTitle),
      description: createElement(
        FormattedMessage,
        messages.recordingInProgressDescription
      ),
    });
  }, []);
}
